// PatchableAssetsPanel.tsx
import { useEffect, useState, useCallback } from "react";
import { X, Loader2, Monitor, ShieldAlert, CheckSquare, Square } from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";

export interface PatchableAsset {
    asset_id: number;
    agent_id: number;
    client_id: number;
    detected_app_name: string;
    installed_version: string;
    latest_version: string;
    hostname: string;
    master_agent_id: number | null;
    ip_address: string;
    mac_address: string;
    os_name: string;
}

interface PatchableAssetsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    clientId: any;
    packageId: number;
    packageName: string;
    search?: string;
}

export default function PatchableAssetsPanel({
    isOpen,
    onClose,
    clientId,
    packageId,
    packageName,
    search,
}: PatchableAssetsPanelProps) {
    const [assets, setAssets] = useState<PatchableAsset[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);

    const fetchAssets = useCallback(async () => {
        if (!isOpen || !clientId || !packageId) return;

        setLoading(true);
        try {
            const queryObj: any = {};
            if (search) {
                queryObj.$or = [
                    { hostname: { $ilike: `%${search}%` } },
                    { ip_address: { $ilike: `%${search}%` } },
                    { mac_address: { $ilike: `%${search}%` } },
                    { os_name: { $ilike: `%${search}%` } },
                    { detected_app_name: { $ilike: `%${search}%` } }
                ];
            }

            const params: any = {
                client_id: clientId,
                package_id: packageId,
                search: search || undefined,
                query: Object.keys(queryObj).length > 0 ? JSON.stringify(queryObj) : undefined
            };

            const res = await baseapi.get("/patch/patchable_assets", { params });

            if (res.data.status) {
                setAssets(res.data.data ?? []);
            } else {
                showError(res.data.message || "Failed to load patchable assets");
            }
        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    }, [isOpen, clientId, packageId, search]);

    useEffect(() => {
        fetchAssets();
        // Reset selections when opened with a new package
        setSelectedAssetIds([]);
    }, [fetchAssets]);

    const handleSelectAll = () => {
        if (selectedAssetIds.length === assets.length) {
            setSelectedAssetIds([]);
        } else {
            setSelectedAssetIds(assets.map(a => a.asset_id));
        }
    };

    const handleSelectOne = (id: number) => {
        setSelectedAssetIds(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const handlePatchSelected = async () => {

        try {
            const resposne = await baseapi.post("/patch/patch_job_by_assets", {
                asset_ids: selectedAssetIds,
                package_id: packageId,

            })
            if (resposne.data.status) {
                showSuccess(resposne.data.message || "Patch job created successfully");
                onClose();
            } else {
                showError(resposne.data.message || "Failed to create patch job");
            }
        }
        catch (e: any) {
            showError(e.message);
        }
    };

    const getOsIcon = (os?: string) => {
        if (!os) return null;
        const value = os.toLowerCase();

        if (value.includes("windows")) return "/windows.png";
        if (value.includes("mac") || value.includes("darwin") || value.includes("os x")) return "/mac.png";
        if (value.includes("linux") || value.includes("ubuntu") || value.includes("debian") || value.includes("centos"))
            return "/linux.svg";

        return null;
    };

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/10 backdrop-blur-sm z-[90] transition-opacity"
                    onClick={onClose}
                />
            )}

            {/* Sliding Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-[35%] min-w-[320px] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl z-[100] transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 shrink-0 bg-gray-50 dark:bg-gray-900/50">
                    <div className="flex flex-col">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-widest flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                            Patch Assets
                        </h2>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 font-mono truncate max-w-[250px]" title={packageName}>
                            {packageName}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scroll p-4 bg-gray-50/40 dark:bg-transparent">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 opacity-60">
                            <Loader2 className="w-6 h-6 animate-spin text-orange-500 dark:text-orange-400" />
                            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                Loading assets...
                            </span>
                        </div>
                    ) : assets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 opacity-40">
                            <Monitor className="w-8 h-8 text-gray-400" />
                            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                No assets found
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between px-1 mb-2">
                                <button
                                    onClick={handleSelectAll}
                                    className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                                >
                                    {selectedAssetIds.length === assets.length ? (
                                        <CheckSquare className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                    ) : (
                                        <Square className="w-4 h-4" />
                                    )}
                                    Select All
                                </button>
                                <span className="text-[10px] text-gray-500 font-bold">
                                    {assets.length} ASSET{assets.length !== 1 ? 'S' : ''}
                                </span>
                            </div>

                            {assets.map((asset) => {
                                const isSelected = selectedAssetIds.includes(asset.asset_id);
                                return (
                                    <div
                                        key={asset.asset_id}
                                        onClick={() => handleSelectOne(asset.asset_id)}
                                        className={`flex flex-col p-3 rounded-xl border transition-all cursor-pointer ${isSelected
                                            ? "bg-orange-500/10 border-orange-500/30"
                                            : "bg-white dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/80 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm"
                                            }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5">
                                                {isSelected ? (
                                                    <CheckSquare className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                                ) : (
                                                    <Square className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                                                        {asset.hostname}
                                                    </h3>
                                                    <div className="flex gap-1">
                                                        <span className="text-[9px] font-mono text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-gray-900 px-1.5 py-0.5 rounded border border-purple-200 dark:border-gray-800">
                                                            IP: {asset.ip_address}
                                                        </span>
                                                        <span className="text-[9px] font-mono text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-gray-900 px-1.5 py-0.5 rounded border border-cyan-200 dark:border-gray-800">
                                                            MAC: {asset.mac_address}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-4 gap-2 mt-2">

                                                    {/* Installed */}
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-widest mb-0.5">
                                                            Installed
                                                        </span>
                                                        <span className="text-[10px] font-mono text-orange-600 dark:text-orange-300 font-semibold">
                                                            {asset.installed_version || "-"}
                                                        </span>
                                                    </div>

                                                    {/* Latest */}
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-widest mb-0.5">
                                                            Latest
                                                        </span>
                                                        <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-300 font-semibold">
                                                            {asset.latest_version || "-"}
                                                        </span>
                                                    </div>

                                                    {/* OS Name → spans 2 columns */}
                                                    <div className="flex flex-col col-span-2">
                                                        <span className="text-[8px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-widest mb-0.5">
                                                            OS Name
                                                        </span>

                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                            {getOsIcon(asset.os_name) && (
                                                                <img
                                                                    src={getOsIcon(asset.os_name)!}
                                                                    alt="os"
                                                                    className="w-3.5 h-3.5 flex-shrink-0"
                                                                />
                                                            )}

                                                            <span className="text-[10px] font-mono font-semibold text-lime-700 dark:text-lime-300 truncate">
                                                                {asset.os_name || "-"}
                                                            </span>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0 shadow-sm">
                    <button
                        onClick={handlePatchSelected}
                        disabled={selectedAssetIds.length === 0 || loading}
                        className="w-full py-2.5 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:border disabled:border-gray-200 dark:disabled:border-gray-700 text-white text-[11px] font-bold uppercase tracking-widest rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm"
                    >
                        <ShieldAlert className="w-3.5 h-3.5" />
                        Patch Selected ({selectedAssetIds.length})
                    </button>
                </div>
            </div>
        </>
    );
}